import { Inject, Injectable, InjectionToken } from '@angular/core';
import { NewRelicEventParams } from '@app/interfaces/new-relic-event.interface';
import { CussService } from '@services/cuss.service';
import { GlobalsService } from './globals.service';
import { Reservation } from '@models/micro';
import { METRIC_NAME, NEW_RELIC_EVENT } from '@app/enums/new-relic.enum';
import { OfflineEventQueueService } from '@app/services/offline-event-queue.service';
import { AdminService } from '@services/admin.service';
import { QuantumMetricService } from '@services/quantum-metric.service';
import { IROPS_STATUS } from '@app/enums/irops.enum';

export const WINDOW = new InjectionToken<Window>('Global window object', {
  factory: () => window,
});

@Injectable({
  providedIn: 'root',
})
export class NewRelicEventsService {
  protected newRelic: NewRelic;
  private NAME_METRIC = METRIC_NAME.KIOSK_AM;

  /** Eventos que se encolan en localStorage cuando no hay red */
  private readonly OFFLINE_ELIGIBLE_EVENTS: Set<string> = new Set([
    NEW_RELIC_EVENT.KIOSK_ADDRESS_AUTOCOMPLETE_SEARCH,
    NEW_RELIC_EVENT.KIOSK_ADDRESS_AUTOCOMPLETE_INIT,
    NEW_RELIC_EVENT.KIOSK_ADDRESS_AUTOCOMPLETE_RESEARCH_INIT,
    NEW_RELIC_EVENT.KIOSK_ADDRESS_AUTOCOMPLETE_SELECTION,
    NEW_RELIC_EVENT.KIOSK_ADDRESS_SCREEN_TIME,
  ]);

  constructor(
    @Inject(WINDOW) private windowRef: any,
    private _cussService: CussService,
    private _globalService: GlobalsService,
    private offlineQueue: OfflineEventQueueService,
    private _adminService: AdminService,
    private _quantumMetricService: QuantumMetricService
  ) {
    this.newRelic = this.windowRef.newrelic;
  }

  /**
   * Sends an event to New Relic with the provided event name and data.
   *
   * @param eventName
   * @param data
   */
  public trackEvent<T extends Record<string, any> = {}>(
    eventName: string,
    data?: T
  ): void {
    setTimeout(() => {
      try {
        if (!this.newRelic) {
          return;
        }

        const finalMetricData: NewRelicEventParams & T = {
          kioskId: this._cussService?.KioskID,
          pnr: this._globalService?.getPNRLookupAWSInt()?.reservationCode,
          branded_fare: this.getReservation()?.flightSummary?.fareFamily,
          name: this.NAME_METRIC,
          subName: eventName,
          stationId: this.getReservation()?.flightSummary?.departureAirport || '',
          station: this._adminService?.admin?.Location || '',
          transactionId: this.getReservation()?.flightSummary?.cartId,
          isService: false,
          isError: false,
          error_message: '',
          QMReplayLinkKiosk: this._quantumMetricService.getReplayLink(),
          ...data,
        };

        if (!this.offlineQueue.isOnline && this.OFFLINE_ELIGIBLE_EVENTS.has(eventName)) {
          this.offlineQueue.enqueue(eventName, finalMetricData);
        } else {
          this.newRelic.addPageAction(eventName, finalMetricData);
        }
      } catch (error) {
      }
    }, 0);
  }

  /**
   * Tracks Check-in events with the specified event name and flight segment details.
   *
   * @param event: NEW_RELIC_EVENT - The name of the event to be tracked.
   * @param currentSegment: Segment - The flight segment details used to determine disruption information.
   */
  public trackCheckInEvent<T extends Record<string, any> = {}>(event: NEW_RELIC_EVENT, data?: T): void {
    const { currentSegment } = this.getReservation()?.flightSummary || {};
    const { flightsNewInfo } = currentSegment || {};
    let dataToSend: Record<string, string> = {};

    switch (flightsNewInfo?.status) {
      case IROPS_STATUS.CANCELLED:
        dataToSend = {
          disruptionLevel: 'major',
          disruptionType: flightsNewInfo?.status,
        };
        break;

      case IROPS_STATUS.DELAYED:
      case IROPS_STATUS.EARLY:
        const timeDifference = Math.floor(
          (new Date(flightsNewInfo.departureDateTime).getTime() -
            new Date(currentSegment.departureDateTime).getTime()) /
            (1000 * 60)
        );
        dataToSend = {
          disruptionLevel: Math.abs(timeDifference) > 30 ? 'major' : 'minor',
          disruptionType: flightsNewInfo?.status,
        };
        break;

      default:
        break;
    }

    this.trackEvent(event, { ...dataToSend, ...data });
  }

  /**
   * Gets the reservation stored in the current session.
   * @returns {Reservation | undefined} - Reservation object or `undefined` if no data is found.
   */
  getReservation(): Reservation | undefined {
    let sessionReservation = sessionStorage.getItem('reservation');
    let reservation = new Reservation();
    if (sessionReservation) {
      sessionReservation = JSON.parse(sessionReservation);
      Object.assign(reservation, sessionReservation);
    }
    return sessionReservation ? reservation : undefined;
  }
}
